/**
 * The three-tier Review verdict (FOUNDATION_REALIGNMENT_PLAN §4.3).
 *
 * The pre-plan verdict was binary on `canFinalize`: an upload with a Loan ID and
 * one non-blank datum showed the green shield, even when 100% of loans were
 * missing charge-off data and credit-loss was 0% ready. PRODUCT.md requires a
 * STRONG warning on low/zero coverage — a gray "worth a look" footnote is not it.
 *
 * This module computes the tier the whole Review screen keys off, from ONE
 * input: per-output READINESS (how many loans each modeled output can produce).
 * The verdict is deliberately COVERAGE-ONLY — it does not read the customer's
 * upload plan (the plan is a UX artifact for the customer's own benefit; the
 * verdict states what the data supports, in the data's own terms). An output at
 * 0% ready is the maximal gap by the share test alone, so it always reads as an
 * amber headline without needing a plan to compare against.
 *
 * Tiers:
 *   - `ready`            — finalizable and no modeled output has a material gap.
 *   - `ready-with-gaps`  — finalizable, but at least one modeled output has a
 *                          material gap (see thresholds). Amber, proceed-able.
 *   - `blocked`          — nothing usable tied to a Loan ID; cannot finalize.
 *
 * Pure + framework-free so BOTH the server (the authority — `upload-review.ts`
 * stores the tier + its reasons on the Review artifact) and any client display
 * read one implementation.
 */

import type { ModeledOutput } from './modeling-readiness';

/**
 * WS-H (F56): the verdict's readiness input — the minimal per-output coverage
 * shape, deliberately DISTINCT from the advisory `ReadinessOutputsSummary`.
 * The Review composer feeds the verdict the PARITY readiness measure (method
 * clauses satisfied per (loan, period) — co-occurrence in one period, the set
 * the derive can actually measure), while the displayed advisory coverage
 * percentages keep the optimistic cross-period union (R9 "keep union"). The
 * advisory summary is a structural subtype, so a caller without a parity
 * measure (fixtures, the pure oracle) can still pass it — but the production
 * composer must not. Tier logic below is unchanged; only the SOURCE changed.
 */
export interface ReviewVerdictOutputReadiness {
  readonly output: ModeledOutput;
  readonly totalLoans: number;
  readonly readyLoans: number;
  /** The output's ready share (0–100). */
  readonly readyPercent: number;
}

export interface ReviewVerdictReadinessInput {
  readonly outputs: readonly ReviewVerdictOutputReadiness[];
}

/** The three verdict tiers, worst-last so a `>` compare orders severity. */
export const REVIEW_VERDICT_TIERS = ['ready', 'ready-with-gaps', 'blocked'] as const;
export type ReviewVerdictTier = (typeof REVIEW_VERDICT_TIERS)[number];

/**
 * Verdict threshold — recorded here as the one place it lives so PRODUCT.md
 * §Review and the tests reference the same number.
 *
 *   - `GAP_UNREADY_SHARE` — when MORE than this share of a modeled output's loans
 *     cannot be produced, that output is a MATERIAL gap and the verdict steps down
 *     from `ready` to `ready-with-gaps`. 0.50 ⇒ "any modeled output < 50% ready ⇒
 *     gaps tier" (a strict-less-than-50%-ready output has a strictly-greater-than-
 *     50% unready share). An output at exactly 0% ready is the maximal case of the
 *     same test (unready share 1.0) and leads the gap ordering.
 */
export const GAP_UNREADY_SHARE = 0.5;

/** One reason an output contributed a gap — surfaced as the headline detail. */
export interface ReviewVerdictGap {
  readonly output: ModeledOutput;
  /** The output's ready share (0–100), for the "N% of loans" phrasing. */
  readonly readyPercent: number;
  readonly readyLoans: number;
  readonly totalLoans: number;
}

/** The computed verdict: the tier + the gaps that produced it (ordered worst-first). */
export interface ReviewVerdict {
  readonly tier: ReviewVerdictTier;
  /**
   * The modeled outputs with a material gap, worst-first (ascending ready
   * share — the emptiest output leads). Empty in the `ready` and `blocked`
   * tiers (blocked's reason is the missing loan-tied data, stated by `canFinalize`).
   */
  readonly gaps: readonly ReviewVerdictGap[];
}

/** Whether an output is a material gap given its readiness. */
function outputIsGap(output: ReviewVerdictOutputReadiness): boolean {
  if (output.totalLoans === 0) return false;
  const unreadyShare = (output.totalLoans - output.readyLoans) / output.totalLoans;
  return unreadyShare > GAP_UNREADY_SHARE;
}

/**
 * Compute the three-tier verdict from finalizability + per-output readiness.
 *
 *   - `canFinalize === false` ⇒ `blocked` (nothing else matters; there is no
 *     usable data to model).
 *   - otherwise, each modeled output is tested for a material gap: any output
 *     whose unready share exceeds {@link GAP_UNREADY_SHARE} is a gap. ANY gap ⇒
 *     `ready-with-gaps`; none ⇒ `ready`.
 *
 * Pure — the single source of truth for both server and client. WS-H (F56): the
 * production composer passes the PARITY readiness measure here (see
 * {@link ReviewVerdictReadinessInput}); the advisory union feeds displays only.
 */
export function computeReviewVerdict(input: {
  readonly canFinalize: boolean;
  readonly readiness: ReviewVerdictReadinessInput;
}): ReviewVerdict {
  if (!input.canFinalize) return { tier: 'blocked', gaps: [] };

  const gaps: ReviewVerdictGap[] = [];
  for (const output of input.readiness.outputs) {
    if (!outputIsGap(output)) continue;
    gaps.push({
      output: output.output,
      readyPercent: output.readyPercent,
      readyLoans: output.readyLoans,
      totalLoans: output.totalLoans,
    });
  }
  // Worst-first: ascending ready share (the emptiest output leads).
  gaps.sort((a, b) => a.readyPercent - b.readyPercent);

  return { tier: gaps.length > 0 ? 'ready-with-gaps' : 'ready', gaps };
}
